import {useOrganization} from 'organization/OrganizationProvider'
import {useEvent} from 'Event/EventProvider'
import {useAsync} from 'lib/async'
import {api} from 'lib/url'
import React, {useCallback} from 'react'

export type Property = {
  name: string
  label: string
  type:
    | 'select'
    | 'text'
    | 'number'
    | 'date'
    | 'checkbox'
    | 'booleancheckbox'
    | 'radio'
    | 'phonenumber'
    | 'textarea'
  read_only: boolean
  options: PropertyOption[]
}

export type PropertyOption = {
  label: string
  value: string
  description: string
}

interface HubspotV2PropertiesProviderContextProps {
  properties: Property[]
  query: (params: {
    types?: Property['type'][]
    readOnly?: boolean
  }) => Property[]
  findProperty: (name: string) => Property | undefined
}

const HubspotV2PropertiesProviderContext = React.createContext<
  undefined | HubspotV2PropertiesProviderContextProps
>(undefined)

export default function HubspotV2PropertiesProvider(props: {
  children: React.ReactElement
}) {
  const {event} = useEvent()
  const {data, loading} = useFetchProperties(event.has_hubspot_v2)

  const properties = data || []

  const query: HubspotV2PropertiesProviderContextProps['query'] = (params) =>
    properties.filter((p) => {
      const matchesType = params.types ? params.types.includes(p.type) : true
      const matchesAccess = params.readOnly ? p.read_only : true

      return matchesType && matchesAccess
    })

  const findProperty: HubspotV2PropertiesProviderContextProps['findProperty'] = (
    name,
  ) => properties.find((p) => p.name === name)

  if (loading) {
    return null
  }

  return (
    <HubspotV2PropertiesProviderContext.Provider
      value={{
        properties,
        query: query,
        findProperty,
      }}
    >
      {props.children}
    </HubspotV2PropertiesProviderContext.Provider>
  )
}

export function useHubspotV2Properties() {
  const context = React.useContext(HubspotV2PropertiesProviderContext)

  if (context === undefined) {
    throw new Error(
      'useHubspotV2Properties must be used within a HubspotV2PropertiesProvider',
    )
  }

  return context
}

function useFetchProperties(shouldFetch: boolean) {
  const {client} = useOrganization()
  const {
    event: {id: eventId},
  } = useEvent()
  const url = api(`/events/${eventId}/integrations/hubspot_v2/properties`)
  const request = useCallback(
    () => (shouldFetch ? client.get<Property[]>(url) : Promise.resolve([])),
    [url, client, shouldFetch],
  )

  return useAsync(request)
}
